import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import AdminModerationTab from '@/components/admin/AdminModerationTab';
import BulkUsersTab from '@/components/admin/BulkUsersTab';
import QrCodesTab from '@/components/admin/QrCodesTab';
import EmojiManagerTab from '@/components/admin/EmojiManagerTab';
import AiBlogGeneratorTab from '@/components/admin/AiBlogGeneratorTab';
import SocialMediaGeneratorTab from '@/components/admin/SocialMediaGeneratorTab';
import NotionSettingsTab from '@/components/admin/NotionSettingsTab';
import IapEventsTab from '@/components/admin/IapEventsTab';
import GscIndexingTab from '@/components/admin/GscIndexingTab';
import SystemHealthTab from '@/components/admin/SystemHealthTab';
import AdminTabsNav from '@/components/admin/AdminTabsNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { SignInProviderPie } from '@/components/SignInProviderPie';
import { PartnerInquiriesCard } from '@/components/admin/PartnerInquiriesCard';
import { HelpBotInsightsCard } from '@/components/admin/HelpBotInsightsCard';
import { ShareStatsCard } from '@/components/admin/ShareStatsCard';
import { Sparkline } from '@/components/Sparkline';
import { ReferralAnalyticsCard } from '@/components/admin/ReferralAnalyticsCard';
import { AnalyticsTabContent } from '@/components/admin/AnalyticsTabContent';
import { SystemStatusCard } from '@/components/admin/SystemStatusCard';
import RefactorWinTile from '@/components/admin/RefactorWinTile';
import AdminPublishingDocsTile from '@/components/admin/AdminDocsTile';
import AdminEmailTemplatesTab from '@/components/admin/AdminEmailTemplatesTab';
import AdminMaintenanceTile from '@/components/admin/AdminMaintenanceTile';
import AdminDeliverabilityTile from '@/components/admin/AdminDeliverabilityTile';
import AdminEmailAlertsTile from '@/components/admin/AdminEmailAlertsTile';
import AdminThankYouWallTile from '@/components/admin/AdminThankYouWallTile';
import AdminSocialIconsTile from '@/components/admin/AdminSocialIconsTile';import { ReferralsTabContent } from '@/components/admin/ReferralsTabContent';
import { UsersTabContent } from '@/components/admin/UsersTabContent';
import { EditUserModal } from '@/components/admin/modals/EditUserModal';
import { ReengagementModal } from '@/components/admin/modals/ReengagementModal';
import { CreateUserModal } from '@/components/admin/modals/CreateUserModal';
import { DeleteUserModal } from '@/components/admin/modals/DeleteUserModal';
import { DirectPasswordModal } from '@/components/admin/modals/DirectPasswordModal';
import { AdminDashboardProvider } from '@/context/AdminDashboardContext';
import { 
  Users, Crown, Download, Plus, Search, Pencil, Trash2, 
  TrendingUp, Eye, UserPlus, RefreshCw, Shield, FlaskConical,
  Gift, CreditCard, Ban, Mail, ShieldPlus, KeyRound, Server,
  CheckCircle2, XCircle, AlertCircle, BarChart3, Flame, Target, Mic, Activity, Bell, PenLine, Clock, QrCode, Sparkles, Share2, Link2
} from 'lucide-react';
import {
  EngagementAnalytics,
  StreakAnalytics,
  ChallengeAnalytics,
  XPAnalytics,
  PhraseAnalytics,
  BadgeAnalytics,
  SubscriptionAnalytics,
  RecentActivity,
  QuickActionsPanel,
  GuestAnalytics,
  TodoAnalytics,
  TimerAnalytics
} from '@/components/admin/AnalyticsDashboard';
import VoiceCloningAdmin from './VoiceCloningAdmin';
import MetaAdsDashboard from '@/components/Admin/MetaAdsDashboard';
import GoogleAdsDashboard from '@/components/Admin/GoogleAdsDashboard';
import PromoCodeAdmin from '@/components/Admin/PromoCodeAdmin';
import { apiFetch } from '@/hooks/useFetch';


export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [referralAnalytics, setReferralAnalytics] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [databaseStatus, setDatabaseStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'users' | 'referrals' | 'analytics' | 'voice'
  
  // Modals
  const [editUser, setEditUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteAdminModal, setShowInviteAdminModal] = useState(false);
  const [inviteAdminEmail, setInviteAdminEmail] = useState('');
  const [isInvitingAdmin, setIsInvitingAdmin] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [showDirectPasswordModal, setShowDirectPasswordModal] = useState(false);
  const [directPasswordUser, setDirectPasswordUser] = useState(null);
  const [newDirectPassword, setNewDirectPassword] = useState('');
  const [isSettingDirectPassword, setIsSettingDirectPassword] = useState(false);
  const [statDetailModal, setStatDetailModal] = useState(null); // For clickable stat cards
  const [purgingCf, setPurgingCf] = useState(false);
  const [oneSignalStats, setOneSignalStats] = useState(null);
  const [showReengagementModal, setShowReengagementModal] = useState(false);
  
  // Create user form
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    subscription_status: 'free',
    is_admin: false,
    is_gifted: false,
    is_contributor: false
  });

  // Check admin access
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/');
      toast.error('Admin access required');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, analyticsRes, referralsRes, emailConfigRes, dbStatusRes, syncStatusRes, oneSignalRes] = await Promise.all([
        apiFetch(`/api/admin/users?search=${searchQuery}&status=${statusFilter}`),
        apiFetch('/api/admin/analytics'),
        apiFetch('/api/admin/referrals/analytics'),
        apiFetch('/api/admin/email-config'),
        apiFetch('/api/admin/database-status'),
        apiFetch('/api/admin/sync-status'),
        apiFetch('/api/notifications/onesignal/status')
      ]);
      
      if (!usersRes.error) {
        setUsers(usersRes.data);
      } else {
        console.error('Failed to fetch users:', usersRes.error);
        setUsers([]);
      }
      
      if (!analyticsRes.error) {
        setAnalytics(analyticsRes.data);
      } else {
        console.error('Failed to fetch analytics:', analyticsRes.error);
        setAnalytics({ total_users: 0, free_users: 0, trial_users: 0, active_subscribers: 0 });
      }
      
      if (!referralsRes.error) {
        setReferralAnalytics(referralsRes.data);
      } else {
        console.error('Failed to fetch referrals:', referralsRes.error);
        setReferralAnalytics({ total_referrals: 0, successful_signups: 0, vip_conversions: 0, top_referrers: [] });
      }
      
      if (!emailConfigRes.error) {
        setSystemStatus(emailConfigRes.data);
      } else {
        console.error('Failed to fetch email config:', emailConfigRes.error);
        setSystemStatus({ resend_api_key_configured: false, error: 'Could not fetch email configuration' });
      }
      
      if (!dbStatusRes.error) {
        setDatabaseStatus(dbStatusRes.data);
      } else {
        console.error('Failed to fetch database status:', dbStatusRes.error);
        setDatabaseStatus({ environment: 'unknown', database_type: 'Unknown', stats: {} });
      }
      
      if (!syncStatusRes.error) {
        setSyncStatus(syncStatusRes.data);
      } else {
        console.error('Failed to fetch sync status:', syncStatusRes.error);
        setSyncStatus({ status: 'error', error: 'Could not fetch sync status' });
      }

      if (!oneSignalRes.error) {
        setOneSignalStats(oneSignalRes.data);
      } else {
        setOneSignalStats(null);
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load data');
      setUsers([]);
      setAnalytics({ total_users: 0, free_users: 0, trial_users: 0, active_subscribers: 0 });
      setSystemStatus({ resend_api_key_configured: false, error: error.message });
      setDatabaseStatus({ environment: 'unknown', database_type: 'Unknown', stats: {} });
      setSyncStatus({ status: 'error', error: error.message });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchData();
    }
  }, [user, fetchData]);

  // Export CSV (uses raw fetch for blob download)
  const handleExport = async () => {
    try {
      const response = await fetch(`/api/admin/export/users`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_export_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Export downloaded');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  // Cloudflare cache purge
  const handlePurgeCloudflare = async () => {
    if (!window.confirm('Purge ALL Cloudflare cache for clearcommunicationapp.com? Users will get fresh assets within seconds.')) return;
    try {
      setPurgingCf(true);
      const { data, error } = await apiFetch('/api/admin/cloudflare/purge-cache', { method: 'POST' });
      if (error) {
        toast.error(error || 'Cloudflare purge failed');
      } else {
        toast.success(data?.message || 'Cloudflare cache purged');
      }
    } catch (e) {
      toast.error('Cloudflare purge failed');
    } finally {
      setPurgingCf(false);
    }
  };

  // Invite a user as a contributor (sets is_contributor=true + sends welcome email)
  const handleInviteContributor = async (u) => {
    if (!u || !u.user_id) return;
    if (!window.confirm(`Invite ${u.name || u.email} as a contributor?\n\nThis will:\n• Grant blog-publishing access\n• Email them a welcome with a link to the editor`)) return;
    try {
      const { data, error } = await apiFetch(`/api/admin/users/${u.user_id}/invite-contributor`, {
        method: 'POST',
      });
      if (error) {
        toast.error(error || 'Failed to invite contributor');
      } else {
        const parts = [];
        parts.push(data?.already_contributor ? 'Already a contributor' : 'Invited!');
        parts.push(data?.email_sent ? 'Welcome email sent.' : `Email skipped (${data?.email_error || 'no email'})`);
        toast.success(parts.join(' '));
        fetchData();
      }
    } catch (e) {
      toast.error('Failed to invite contributor');
    }
  };

  // OneSignal test push to current admin only
  const [sendingTestPush, setSendingTestPush] = useState(false);
  const handleSendTestPush = async () => {
    if (!user?.user_id) {
      toast.error('No admin user_id available');
      return;
    }
    try {
      setSendingTestPush(true);
      const { data, error } = await apiFetch('/api/notifications/onesignal/send', {
        method: 'POST',
        body: {
          title: 'ClearCommunicationApp Test',
          content: `Test push at ${new Date().toLocaleTimeString()} — if you see this, OneSignal is working!`,
          external_user_ids: [user.user_id],
          url: window.location.origin + '/account',
        },
      });
      if (error) {
        toast.error(error || 'Test push failed');
      } else {
        toast.success('Test push sent! Check your device — should arrive within seconds.');
      }
    } catch (e) {
      toast.error('Test push failed');
    } finally {
      setSendingTestPush(false);
    }
  };

  // Create user
  const handleCreateUser = async () => {
    // Client-side validation so admins see a clear message immediately
    // instead of the generic "Request failed (422)" toast from FastAPI's
    // EmailStr / Field required validators.
    const trimmedName = (newUser.name || '').trim();
    const trimmedEmail = (newUser.email || '').trim();
    const password = newUser.password || '';
    if (!trimmedName) {
      toast.error('Name is required');
      return;
    }
    if (!trimmedEmail) {
      toast.error('Email is required');
      return;
    }
    // Loose email check — server still does the authoritative validation.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error('Please enter a valid email address (e.g. name@example.com)');
      return;
    }
    if (!password || password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      const { error } = await apiFetch('/api/admin/users', {
        method: 'POST',
        body: { ...newUser, name: trimmedName, email: trimmedEmail }
      });

      if (!error) {
        toast.success('User created successfully');
        setShowCreateModal(false);
        setNewUser({ name: '', email: '', password: '', subscription_status: 'free', is_admin: false, is_gifted: false, is_contributor: false });
        fetchData();
      } else {
        toast.error(error || 'Failed to create user');
      }
    } catch (error) {
      toast.error('Failed to create user');
    }
  };

  // Update user
  const handleUpdateUser = async () => {
    if (!editUser) return;
    
    try {
      const { data, error } = await apiFetch(`/api/admin/users/${editUser.user_id}`, {
        method: 'PUT',
        body: {
          name: editUser.name,
          subscription_status: editUser.subscription_status,
          subscription_tier: editUser.subscription_tier,
          is_admin: editUser.is_admin,
          is_gifted: editUser.is_gifted,
          is_contributor: editUser.is_contributor
        }
      });
      
      if (!error) {
        toast.success('User updated successfully');
        setEditUser(null);
        fetchData();
      } else {
        toast.error(error || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  // Make subscription free (gift)
  const handleMakeFree = async (userId) => {
    try {
      const { data, error } = await apiFetch('/api/admin/make-free', {
        method: 'POST',
        body: { user_id: userId }
      });
      
      if (!error) {
        toast.success('Subscription marked as free (gifted)');
        fetchData();
      } else {
        toast.error(error || 'Failed to gift subscription');
      }
    } catch (error) {
      toast.error('Failed to gift subscription');
    }
  };

  // Revoke gifted subscription
  const handleRevokeFree = async (userId) => {
    try {
      const { data, error } = await apiFetch('/api/admin/revoke-free', {
        method: 'POST',
        body: { user_id: userId }
      });
      
      if (!error) {
        toast.success('Gifted subscription revoked');
        fetchData();
      } else {
        toast.error(error || 'Failed to revoke gift');
      }
    } catch (error) {
      toast.error('Failed to revoke gift');
    }
  };

  // Get payment method display
  const getPaymentMethod = (u) => {
    if (u.is_gifted) return { label: 'Gifted', color: 'bg-purple-500' };
    if (u.stripe_subscription_id) return { label: 'Stripe', color: 'bg-blue-500' };
    if (u.subscription_status === 'free') return { label: 'Free', color: 'bg-gray-400' };
    if (u.subscription_status === 'trial') return { label: 'Trial', color: 'bg-cyan-500' };
    return { label: 'None', color: 'bg-gray-300' };
  };

  // Invite admin
  const handleInviteAdmin = async () => {
    if (!inviteAdminEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setIsInvitingAdmin(true);
    
    try {
      const { data, error } = await apiFetch('/api/admin/invite-admin', {
        method: 'POST',
        body: {
          email: inviteAdminEmail,
          origin_url: window.location.origin
        }
      });
      
      if (!error) {
        if (data.method === 'instant') {
          toast.success(`${inviteAdminEmail} is now an admin!`);
        } else if (data.method === 'email_invite') {
          toast.success(`Admin invitation sent to ${inviteAdminEmail}`);
        } else {
          toast.info(data.message);
        }
        setShowInviteAdminModal(false);
        setInviteAdminEmail('');
        fetchData();
      } else {
        toast.error(error || 'Failed to invite admin');
      }
    } catch (error) {
      toast.error('Failed to invite admin');
    } finally {
      setIsInvitingAdmin(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    
    try {
      const { data, error } = await apiFetch(`/api/admin/users/${deleteUser.user_id}`, {
        method: 'DELETE'
      });
      
      if (!error) {
        toast.success('User deleted successfully');
        setDeleteUser(null);
        fetchData();
      } else {
        toast.error(error || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  // Send password reset email
  const handleSendPasswordReset = async () => {
    if (!passwordResetUser) return;
    
    setIsSendingReset(true);
    
    try {
      const { data, error } = await apiFetch('/api/admin/send-password-reset', {
        method: 'POST',
        body: { email: passwordResetUser.email }
      });
      
      if (!error) {
        toast.success(`Password reset email sent to ${passwordResetUser.email}`);
        setPasswordResetUser(null);
      } else {
        toast.error(error || 'Failed to send password reset email');
      }
    } catch (error) {
      toast.error('Failed to send password reset email');
    } finally {
      setIsSendingReset(false);
    }
  };

  // Set password directly (when email isn't working)
  const handleSetDirectPassword = async () => {
    if (!directPasswordUser || !newDirectPassword) return;
    
    if (newDirectPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsSettingDirectPassword(true);
    
    try {
      const { data, error } = await apiFetch('/api/admin/set-user-password', {
        method: 'POST',
        body: { 
          email: directPasswordUser.email,
          new_password: newDirectPassword
        }
      });
      
      if (!error) {
        toast.success(`Password updated for ${directPasswordUser.email}`);
        setShowDirectPasswordModal(false);
        setDirectPasswordUser(null);
        setNewDirectPassword('');
      } else {
        toast.error(error || 'Failed to set password');
      }
    } catch (error) {
      toast.error('Failed to set password');
    } finally {
      setIsSettingDirectPassword(false);
    }
  };

  // Status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'trial': return 'bg-blue-500';
      case 'free': return 'bg-gray-500';
      case 'expired': return 'bg-orange-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  if (authLoading || !user?.is_admin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <RefreshCw className="h-8 w-8 animate-spin text-[#00E5CC]" />
        </div>
      </div>
    );
  }

  return (
    <AdminDashboardProvider value={{
      users,
      currentUser: user,
      loading,
      searchQuery,
      setSearchQuery,
      statusFilter,
      setStatusFilter,
      setShowCreateModal,
      handleExport,
      setEditUser,
      setPasswordResetUser,
      handleInviteContributor,
      setDeleteUser,
      setDirectPasswordUser,
      setShowDirectPasswordModal,
      handleMakeFree,
      handleRevokeFree,
      getStatusColor,
      getPaymentMethod,
      setShowReengagementModal,
      fetchData,
    }}>
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-950">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-testid="admin-dashboard-title">
              <Shield className="h-6 w-6 md:h-8 md:w-8 text-[#00E5CC]" />
              Admin Dashboard
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1 text-sm md:text-base">Manage users and subscriptions</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setShowInviteAdminModal(true)} variant="outline" size="sm" data-testid="invite-admin-btn" className="flex-1 md:flex-none">
              <ShieldPlus className="h-4 w-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Invite</span> Admin
            </Button>
            <Button onClick={() => navigate('/admin/test')} variant="outline" size="sm" data-testid="test-env-btn" className="flex-1 md:flex-none">
              <FlaskConical className="h-4 w-4 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Test</span> Env
            </Button>
            <Button onClick={handlePurgeCloudflare} variant="outline" size="sm" data-testid="purge-cf-btn" className="flex-1 md:flex-none" disabled={purgingCf}>
              <RefreshCw className={`h-4 w-4 mr-1 md:mr-2 ${purgingCf ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Purge</span> CF Cache
            </Button>
            <Button onClick={handleSendTestPush} variant="outline" size="sm" data-testid="test-push-btn" className="flex-1 md:flex-none" disabled={sendingTestPush}>
              <Bell className={`h-4 w-4 mr-1 md:mr-2 ${sendingTestPush ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">Test</span> Push
              {oneSignalStats?.configured && (
                <span
                  className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#00E5CC]/15 text-[#008374] dark:bg-[#00E5CC]/20 dark:text-[#00E5CC]"
                  title={`${oneSignalStats.messageable_players ?? 0} subscribed of ${oneSignalStats.players ?? 0} devices`}
                  data-testid="onesignal-subs-badge"
                >
                  {oneSignalStats.messageable_players ?? 0}
                </span>
              )}
            </Button>
            <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn" className="flex-1 md:flex-none">
              <RefreshCw className="h-4 w-4 mr-1 md:mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Tab Navigation — extracted to AdminTabsNav.jsx */}
        <AdminTabsNav activeTab={activeTab} onTabChange={setActiveTab} />

        {/* Bulk Create + Welcome Email template */}
        {activeTab === 'bulk' && (
          <BulkUsersTab />
        )}

        {/* QR Code generator + scan tracker */}
        {activeTab === 'qr' && (
          <QrCodesTab />
        )}

        {/* Communication Emojis manager — pack CRUD + multi-file upload */}
        {activeTab === 'emojis' && (
          <EmojiManagerTab />
        )}

        {/* AI Blog Generator (Claude Sonnet 4.5) */}
        {activeTab === 'ai-blog' && (
          <AiBlogGeneratorTab />
        )}

        {/* Social Media Generator (Nano Banana + Claude) */}
        {activeTab === 'social' && (
          <SocialMediaGeneratorTab />
        )}

        {/* Notion integration settings */}
        {activeTab === 'notion' && (
          <NotionSettingsTab />
        )}

        {/* IAP Events (Apple ASN V2 + Google RTDN) */}
        {activeTab === 'iap' && (
          <IapEventsTab />
        )}

        {/* GSC Indexing API — accelerate Google re-crawl after canonical fix */}
        {activeTab === 'gsc' && (
          <GscIndexingTab />
        )}

        {/* System Health — single aggregate view of cron jobs, drainer, tokens */}
        {activeTab === 'health' && (
          <SystemHealthTab />
        )}

        {/* Moderation Tab Content — pending blog posts + contributor requests */}
        {activeTab === 'moderation' && (
          <AdminModerationTab />
        )}

        {activeTab === 'emails' && (
          <AdminEmailTemplatesTab />
        )}

        {/* Overview Tab Content */}
        {activeTab === 'overview' && (
          <>
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <Card 
              className="cursor-pointer hover:shadow-md hover:border-[#00E5CC]/50 transition-all"
              onClick={() => setStatDetailModal({ type: 'total_users', title: 'Total Users', value: analytics.total_users, description: 'All registered users in the system', breakdown: users })}
              data-testid="stat-total-users"
            >
              <CardHeader className="pb-2">
                <CardDescription>Total Users</CardDescription>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#00E5CC]" />
                  {analytics.total_users}
                </CardTitle>
              </CardHeader>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-md hover:border-yellow-500/50 transition-all"
              onClick={() => setStatDetailModal({ type: 'subscribers', title: 'Active Subscribers (Paid)', value: analytics.active_subscribers, description: 'Paid VIP subscriptions (excludes gifted)', breakdown: users.filter(u => u.subscription_status === 'active' && !u.is_gifted) })}
              data-testid="stat-subscribers"
            >
              <CardHeader className="pb-2">
                <CardDescription>Paid Subscribers</CardDescription>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Crown className="h-5 w-5 text-yellow-500" />
                  {analytics.active_subscribers}
                </CardTitle>
              </CardHeader>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-md hover:border-blue-500/50 transition-all"
              onClick={() => setStatDetailModal({ type: 'trial', title: 'Trial Users', value: analytics.trial_users, description: 'Users currently on trial period', breakdown: users.filter(u => u.subscription_status === 'trial') })}
              data-testid="stat-trial"
            >
              <CardHeader className="pb-2">
                <CardDescription>Trial Users</CardDescription>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                  {analytics.trial_users}
                </CardTitle>
              </CardHeader>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-md hover:border-gray-400/50 transition-all"
              onClick={() => setStatDetailModal({ type: 'free', title: 'Free Users', value: analytics.free_users, description: 'Users on free tier with daily limits', breakdown: users.filter(u => u.subscription_status === 'free' || !u.subscription_status) })}
              data-testid="stat-free"
            >
              <CardHeader className="pb-2">
                <CardDescription>Free Users</CardDescription>
                <CardTitle className="text-2xl">{analytics.free_users}</CardTitle>
              </CardHeader>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-md hover:border-purple-500/50 transition-all"
              onClick={() => setStatDetailModal({ type: 'phrases', title: 'Phrases Viewed', value: analytics.total_phrases_viewed, description: 'Total phrase lookups across all users' })}
              data-testid="stat-phrases"
            >
              <CardHeader className="pb-2">
                <CardDescription>Phrases Viewed</CardDescription>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Eye className="h-5 w-5 text-purple-500" />
                  {analytics.total_phrases_viewed}
                </CardTitle>
                {analytics.phrase_saves_per_day_14d && analytics.phrase_saves_per_day_14d.length >= 2 && (
                  <div className="pt-1" title="Daily phrase saves (engagement proxy), last 14 days">
                    <Sparkline
                      values={analytics.phrase_saves_per_day_14d.map(d => d.count)}
                      width={140}
                      height={32}
                      color="#a855f7"
                      data-testid="phrase-saves-sparkline"
                    />
                  </div>
                )}
              </CardHeader>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-md hover:border-green-500/50 transition-all"
              onClick={() => setStatDetailModal({ type: 'new', title: 'New Users (7 days)', value: analytics.recent_signups, description: 'Users who signed up in the last 7 days' })}
              data-testid="stat-new"
            >
              <CardHeader className="pb-2">
                <CardDescription>New (7 days)</CardDescription>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-green-500" />
                  {analytics.recent_signups}
                </CardTitle>
                {analytics.signups_per_day_14d && analytics.signups_per_day_14d.length >= 2 && (
                  <div className="pt-1" title="Daily signups, last 14 days">
                    <Sparkline
                      values={analytics.signups_per_day_14d.map(d => d.count)}
                      width={140}
                      height={32}
                      color="#22c55e"
                      data-testid="signups-sparkline"
                    />
                  </div>
                )}
              </CardHeader>
            </Card>
          </div>
        )}

        {/* Sign-in Methods Pie Chart */}
        {analytics?.users_by_provider && (
          <SignInProviderPie usersByProvider={analytics.users_by_provider} />
        )}

        {/* Last Engagement Push (OneSignal cron) */}
        {oneSignalStats?.configured && (
          <Card className="mb-8" data-testid="engagement-push-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell className="h-4 w-4 text-[#00E5CC]" />
                  Last Engagement Push
                </CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm('Run the weekly engagement push NOW? This will send personalized pushes to active subscribers.')) return;
                    const t = toast.loading('Running engagement push…');
                    const { data, error } = await apiFetch('/api/notifications/onesignal/run-engagement-push', { method: 'POST' });
                    toast.dismiss(t);
                    if (error) {
                      toast.error(error || 'Engagement push failed');
                    } else {
                      const s = data?.summary || {};
                      toast.success(`Sent ${s.sent ?? 0} · skipped ${s.skipped ?? 0} · errors ${s.errors ?? 0}`);
                      fetchData();
                    }
                  }}
                  data-testid="run-engagement-push-btn"
                >
                  Run Now
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {oneSignalStats.last_engagement_push ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">When</p>
                      <p className="font-medium" data-testid="last-push-when">
                        {new Date(oneSignalStats.last_engagement_push.ran_at).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Sent</p>
                      <p className="font-medium text-green-600 dark:text-green-400" data-testid="last-push-sent">
                        {oneSignalStats.last_engagement_push.sent}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Skipped</p>
                      <p className="font-medium" data-testid="last-push-skipped">
                        {oneSignalStats.last_engagement_push.skipped}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Errors</p>
                      <p className={`font-medium ${oneSignalStats.last_engagement_push.errors > 0 ? 'text-red-600 dark:text-red-400' : ''}`} data-testid="last-push-errors">
                        {oneSignalStats.last_engagement_push.errors}
                      </p>
                    </div>
                  </div>

                  {/* Sparkline trend (last 8 runs) */}
                  {oneSignalStats.recent_engagement_push_runs && oneSignalStats.recent_engagement_push_runs.length >= 2 && (
                    <div className="flex items-center gap-3 pt-2 border-t border-border">
                      <div>
                        <p className="text-xs text-muted-foreground">Send trend (last {oneSignalStats.recent_engagement_push_runs.length} runs)</p>
                        <Sparkline
                          values={oneSignalStats.recent_engagement_push_runs.map(r => r.sent || 0)}
                          width={200}
                          height={44}
                          data-testid="engagement-push-sparkline"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        {(() => {
                          const runs = oneSignalStats.recent_engagement_push_runs;
                          const latest = runs[runs.length - 1]?.sent || 0;
                          const prev = runs[runs.length - 2]?.sent || 0;
                          const delta = latest - prev;
                          const pct = prev > 0 ? Math.round((delta / prev) * 100) : null;
                          const trend = delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
                          const color = delta > 0 ? 'text-green-600 dark:text-green-400' : delta < 0 ? 'text-red-600 dark:text-red-400' : '';
                          return (
                            <>
                              <p className={`text-sm font-medium ${color}`}>
                                {trend} {Math.abs(delta)} vs. previous
                              </p>
                              {pct !== null && (
                                <p className={color}>{pct >= 0 ? '+' : ''}{pct}%</p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Never run. Cron fires every Friday 6 PM CT. Click "Run Now" to test.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Partner inquiries inbox */}
        <div className="mb-8">
          <PartnerInquiriesCard />
        </div>

        {/* Detective Dino top questions */}
        <HelpBotInsightsCard />

        {/* Engagement: shares + referrals
            Note: the compact `<ReferralAnalyticsCard />` summary used to
            live here too, but the full "Invite a Friend! Analytics" block
            below covers the same numbers in more detail, so we render
            only the larger version to remove duplication. */}
        <ShareStatsCard />

        {/* System Status Card — extracted to its own component for
            maintainability. State is owned by this page; see top of
            file for the fetch logic. */}
        <SystemStatusCard
          systemStatus={systemStatus}
          databaseStatus={databaseStatus}
          syncStatus={syncStatus}
          analytics={analytics}
          loading={loading}
        />

        {/* Refactor Win Tile — codebase line count for ops visibility */}
        <RefactorWinTile />
        <AdminPublishingDocsTile />
        <AdminMaintenanceTile />
        <AdminDeliverabilityTile />
        <AdminEmailAlertsTile />
        <AdminThankYouWallTile />
        <AdminSocialIconsTile />

        {/* Referral Analytics */}
        {referralAnalytics && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
              <Gift className="h-5 w-5 text-[#8B5CF6]" />
              Invite a Friend! Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
              <Card className="border-[#8B5CF6]/30">
                <CardHeader className="pb-2">
                  <CardDescription>Total Friends Invited</CardDescription>
                  <CardTitle className="text-2xl text-[#8B5CF6]">
                    {referralAnalytics.total_referrals}
                  </CardTitle>
                </CardHeader>
              </Card>
              
              <Card className="border-green-500/30">
                <CardHeader className="pb-2">
                  <CardDescription>Signups</CardDescription>
                  <CardTitle className="text-2xl text-green-500">
                    {referralAnalytics.successful_signups}
                  </CardTitle>
                </CardHeader>
              </Card>
              
              <Card className="border-yellow-500/30">
                <CardHeader className="pb-2">
                  <CardDescription>VIP Conversions</CardDescription>
                  <CardTitle className="text-2xl text-yellow-500">
                    {referralAnalytics.vip_conversions}
                  </CardTitle>
                </CardHeader>
              </Card>
              
              <Card className="border-[#00E5CC]/30">
                <CardHeader className="pb-2">
                  <CardDescription>Credits Earned</CardDescription>
                  <CardTitle className="text-2xl text-[#00E5CC]">
                    {referralAnalytics.total_credits_earned} mo
                  </CardTitle>
                </CardHeader>
              </Card>
              
              <Card className="border-blue-500/30">
                <CardHeader className="pb-2">
                  <CardDescription>Credits Used</CardDescription>
                  <CardTitle className="text-2xl text-blue-500">
                    {referralAnalytics.total_credits_used} mo
                  </CardTitle>
                </CardHeader>
              </Card>
            </div>
            
            {/* Top Referrers Table */}
            {referralAnalytics.top_referrers?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Top Referrers</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead className="text-center">Total Friends Invited</TableHead>
                        <TableHead className="text-center">VIP Conversions</TableHead>
                        <TableHead className="text-center">Credits Earned</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {referralAnalytics.top_referrers.map((referrer) => (
                        <TableRow key={referrer.email}>
                          <TableCell className="font-medium">{referrer.email}</TableCell>
                          <TableCell className="text-center">{referrer.total_referrals}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={referrer.vip_referrals > 0 ? "default" : "secondary"}>
                              {referrer.vip_referrals}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center text-[#00E5CC] font-medium">
                            {referrer.credits_earned} mo
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        )}
          </>
        )}

        {/* Analytics Tab Content */}
        {activeTab === 'analytics' && (
          <AnalyticsTabContent />
        )}

        {/* Users Tab Content — extracted to UsersTabContent.jsx (consumes AdminDashboardContext) */}
        {activeTab === 'users' && (
          <UsersTabContent />
        )}

        {/* Referrals Tab Content — extracted to ReferralsTabContent.jsx */}
        {activeTab === 'referrals' && (
          <ReferralsTabContent referralAnalytics={referralAnalytics} />
        )}

        {/* Voice Cloning Tab Content */}
        {activeTab === 'voice' && (
          <VoiceCloningAdmin />
        )}

        {/* Ad Analytics Tab Content */}
        {activeTab === 'ads' && (
          <div className="space-y-8">
            <MetaAdsDashboard />
            <div className="border-t border-border pt-8">
              <GoogleAdsDashboard />
            </div>
          </div>
        )}

        {/* Promo Codes Tab Content */}
        {activeTab === 'promo' && (
          <PromoCodeAdmin />
        )}
      </main>

      {/* Create User Modal — extracted to components/admin/modals/CreateUserModal.jsx */}
      <CreateUserModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        newUser={newUser}
        setNewUser={setNewUser}
        onSubmit={handleCreateUser}
      />

      {/* Edit User Modal — extracted to components/admin/modals/EditUserModal.jsx */}
      <EditUserModal
        user={editUser}
        setUser={setEditUser}
        onSave={handleUpdateUser}
        onSendReset={(u) => {
          setPasswordResetUser(u);
          setEditUser(null);
        }}
        onSetPasswordDirect={(u) => {
          setDirectPasswordUser(u);
          setShowDirectPasswordModal(true);
          setEditUser(null);
        }}
      />

      {/* Bulk Re-engagement Modal */}
      <ReengagementModal
        open={showReengagementModal}
        onOpenChange={setShowReengagementModal}
        onSent={() => fetchData()}
      />

      {/* Delete Confirmation — extracted to components/admin/modals/DeleteUserModal.jsx */}
      <DeleteUserModal
        user={deleteUser}
        setUser={setDeleteUser}
        onConfirm={handleDeleteUser}
      />

      {/* Password Reset Confirmation Dialog */}
      <AlertDialog open={!!passwordResetUser} onOpenChange={(open) => !open && setPasswordResetUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-500" />
              Send Password Reset Email
            </AlertDialogTitle>
            <AlertDialogDescription>
              Send a password reset email to <strong>{passwordResetUser?.email}</strong>?
              <br /><br />
              They will receive a link to reset their password (valid for 24 hours).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSendingReset}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSendPasswordReset} 
              disabled={isSendingReset}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {isSendingReset ? 'Sending...' : 'Send Reset Email'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite Admin Modal */}
      <Dialog open={showInviteAdminModal} onOpenChange={setShowInviteAdminModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldPlus className="h-5 w-5 text-[#00E5CC]" />
              Invite Admin
            </DialogTitle>
            <DialogDescription>
              Invite someone to be an admin. If they have an account, they&apos;ll be made admin instantly. Otherwise, they&apos;ll receive an email invitation to create an account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="admin@example.com"
                  className="pl-10"
                  value={inviteAdminEmail}
                  onChange={(e) => setInviteAdminEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded-lg text-sm text-blue-800 dark:text-blue-300">
              <p className="font-medium mb-1">What happens next?</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-400">
                <li><strong>Existing user:</strong> Instantly granted admin access + notification email</li>
                <li><strong>New user:</strong> Receives email invite to create account with admin privileges</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteAdminModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleInviteAdmin} disabled={isInvitingAdmin || !inviteAdminEmail}>
              {isInvitingAdmin ? 'Sending...' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Direct Password Set Modal — extracted to components/admin/modals/DirectPasswordModal.jsx */}
      <DirectPasswordModal
        open={showDirectPasswordModal}
        onOpenChange={(open) => {
          setShowDirectPasswordModal(open);
          if (!open) {
            setNewDirectPassword('');
            setDirectPasswordUser(null);
          }
        }}
        user={directPasswordUser}
        newPassword={newDirectPassword}
        setNewPassword={setNewDirectPassword}
        isSubmitting={isSettingDirectPassword}
        onSubmit={handleSetDirectPassword}
      />

      {/* Stat Detail Modal */}
      <Dialog open={!!statDetailModal} onOpenChange={(open) => !open && setStatDetailModal(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              {statDetailModal?.type === 'total_users' && <Users className="h-6 w-6 text-[#00E5CC]" />}
              {statDetailModal?.type === 'subscribers' && <Crown className="h-6 w-6 text-yellow-500" />}
              {statDetailModal?.type === 'trial' && <TrendingUp className="h-6 w-6 text-blue-500" />}
              {statDetailModal?.type === 'free' && <Users className="h-6 w-6 text-gray-500" />}
              {statDetailModal?.type === 'phrases' && <Eye className="h-6 w-6 text-purple-500" />}
              {statDetailModal?.type === 'new' && <UserPlus className="h-6 w-6 text-green-500" />}
              {statDetailModal?.title}
            </DialogTitle>
            <DialogDescription>{statDetailModal?.description}</DialogDescription>
          </DialogHeader>
          
          <div className="py-4 flex-1 overflow-auto">
            <div className="text-center mb-6">
              <p className="text-5xl font-bold text-[#00E5CC]">{statDetailModal?.value}</p>
              <p className="text-sm text-muted-foreground mt-1">{statDetailModal?.title}</p>
            </div>
            
            {statDetailModal?.breakdown && statDetailModal.breakdown.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statDetailModal.breakdown.slice(0, 20).map((u) => (
                      <TableRow key={u.email || u.user_id}>
                        <TableCell className="font-medium text-sm">{u.email}</TableCell>
                        <TableCell className="text-sm">{u.name || '—'}</TableCell>
                        <TableCell>
                          <Badge variant={
                            u.subscription_status === 'active' ? 'default' :
                            u.subscription_status === 'trial' ? 'secondary' : 'outline'
                          } className="text-xs">
                            {u.subscription_status || 'free'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {statDetailModal.breakdown.length > 20 && (
                  <div className="p-3 text-center text-sm text-muted-foreground bg-muted/30">
                    Showing 20 of {statDetailModal.breakdown.length} users. Go to Users tab for full list.
                  </div>
                )}
              </div>
            )}
            
            {statDetailModal?.type === 'phrases' && (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-500">{Math.round(statDetailModal.value / (analytics?.total_users || 1))}</p>
                    <p className="text-xs text-muted-foreground">Avg. per User</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg text-center">
                    <p className="text-2xl font-bold text-[#00E5CC]">{analytics?.total_users || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Users</p>
                  </div>
                </div>
                <p className="text-muted-foreground text-center">
                  Users are actively looking up phrases to better understand communication!
                </p>
              </div>
            )}
            
            {statDetailModal?.type === 'new' && (
              <div className="text-center text-sm text-muted-foreground">
                <p>New signups from the past 7 days show your app's growth!</p>
                <p className="mt-2">Consider running promotions to boost this number.</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatDetailModal(null)}>Close</Button>
            {statDetailModal?.breakdown && (
              <Button onClick={() => { setStatDetailModal(null); setActiveTab('users'); }}>
                View All Users
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </AdminDashboardProvider>
  );
}
